import mitt from "mitt";
import * as hisCode from "@/api/hisCode.js";
import _ from "lodash";
import { Message } from "@arco-design/web-vue"
import useI18n from "@/i18n/index.js";
import Encrypt from "jsencrypt";
const { t } = useI18n.global;

/**
 * bus事件总线
 */
export const bus = mitt();

/**
 * 密码加密
 */
const pwdPublicKey = `-----BEGIN PUBLIC KEY-----MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQDCFRI3ES7vV66BMDGPe1SmQx0LB5trmQFydNTMbjN6hVvz9nHfjXKIZQrYbj6R84IN54rp2XwIFlBiJzRD5ZMTpHEo4JSZkvH+m79N2bn/1XMBH2UYAULzO8le/9d87OjfDA8rZSvcdssgWbQagceku/TyXhbhtlbjA6cM75OQCQIDAQAB-----END PUBLIC KEY-----`
export function pwdEncrypt(pwd) {
    const _Encrypt = new Encrypt();
    _Encrypt.setPublicKey(pwdPublicKey);
    return _Encrypt.encrypt(pwd);
}

/**
 * 生成随机数
 */
export function randomNumber(digits = 6, arr) {
    const min = 10 ** (digits - 1);
    const max = (10 ** digits) - 1;
    let randomNum = Math.floor(Math.random() * (max - min + 1) + min);
    if (!arr) return randomNum;
    while (arr.includes(randomNum)) {
        randomNum = Math.floor(Math.random() * (max - min + 1) + min);
    }
    return randomNum;
}

/**
 * 生成随机字符串
 */
export function randomString(len = 8) {
    const $chars = "ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678";
    const maxPos = $chars.length;
    let pwd = "";
    for (let i = 0; i < len; i++) {
        pwd = pwd + $chars.charAt(Math.floor(Math.random() * maxPos));
    }
    return pwd;
}

/**
 * 获取字典值
 * @param {String} key 字典key
 * @param {Object} params 接口入参
 * 
 * @return {Promise} 接口返回值
 */
export function getHisCode(key, params) {
    return new Promise((resolve, reject) => {
        const hisList = Object.keys(hisCode);
        // 判断是否存在
        const is = hisList.find(api => hisCode[api].prototype.name === key);
        if (!is) return resolve([]);
        // 请求接口
        hisList.forEach(api => {
            const name = hisCode[api].prototype.name;
            if (name === key) {
                hisCode[api](params).then((res) => {
                    resolve(res?.data?.items || res?.data || res);
                }).catch(err => {
                    reject(err)
                })
            }
        })
    })
}

/**
 * 携带多个参数的Promise任务队列
 */
export class TaskQueue {
    constructor() {
        this.tasks = [];
    }
    // 添加任务
    add = (fn, ...args) => {
        return new Promise((resolve, reject) => {
            this.tasks.push({ fn, args, resolve, reject });
            if (this.tasks.length === 1) {
                this.run();
            }
        });
    }
    // 执行任务
    run = () => {
        if (this.tasks.length === 0) return;
        const { fn, args, resolve, reject } = this.tasks[0];
        fn(...args)
            .then((res) => {
                resolve(res);
                this.tasks.shift();
                this.run();
            })
            .catch((err) => {
                reject(err);
                this.tasks.shift();
                this.run();
            });
    }
}

/**
 * 递归分类
 */
export function recursiveCategorize(items, parentKey, selfKey, value = null) {
    const result = [];
    items.forEach((item) => {
        if (item[parentKey] === value) {
            const children = recursiveCategorize(items, parentKey, selfKey, item[selfKey]);
            children.forEach(item => {
                item.isChildren = true
            })
            result.push({
                ...item,
                children,
            });
        }
    });
    return result;
}

/**
 * 病历处方校验判断
 * @param {Object[]} filterData 格式化后的数据
 * @param {Object} jumpActiveIndex 跳转到的tab(ref)
 * @param {Object} showData 显隐的数据
 * @param {Boolean} showData.westernMedicine 西成药
 * @param {Boolean} showData.chineseHerbal 中药
 * @param {Boolean} showData.injectionView 输注
 * @param {Boolean} showData.inspectionView 检验检查
 * @param {Boolean} showData.disposalView 治疗
 * @param {Boolean} showData.operationView 手术
 * @param {Boolean} isTemplate 是否是模板: 模板不判断诊断
 * 
 * @return {Boolean}
 */
export function checkPrescription(filterData, jumpActiveIndex, showData, isTemplate = false) {
    let flag = true;
    let jumpKey = [];
    let tips = [];
    const showIndex = {
        westernMedicine: 1,
        chineseHerbal: 2,
        injectionView: 3,
        inspectionView: 4,
        disposalView: 5,
        operationView: 6,
    }
    let arr = [];
    Object.keys(showData).forEach((key) => {
        if (!showData[key]) return;
        arr.push(key);
        showIndex[key] = arr.length - 1;
    })
    const {
        soap, // 病历数据
        diagnoses, // 诊断
        pageGroupWesternMedicine, // 西成药
        pageGroupHerbalMedicine, // 中药
        pageGroupInjectionMedicine, // 输注
        pageGroupInspection, // 检验检查
        pageGroupTreatment, // 治疗
        pageGroupSurgery, // 手术
    } = filterData;
    // 舒张压、收缩压的格式判断
    const { soapDiastolicPressure, soapSystolicPressure } = soap;
    if ((soapDiastolicPressure || soapSystolicPressure) && (!soapDiastolicPressure || !soapSystolicPressure)) {
        flag = false;
        jumpKey.push(0);
        tips.push(t('prescriptionTable.medicalRecords.bloodMsg'))
    }
    // 诊断
    const isDiagnoses = diagnoses?.length > 0;
    if (!isDiagnoses && !isTemplate) {
        flag = false;
        jumpKey.push(0);
        tips.push(t('prescriptionTable.medicalRecords.requiredMsg'));
    }
    // 西成药: 剂量，剂量单位，频率，途径，天数，总量，总量单位
    pageGroupWesternMedicine?.map((item) => {
        item.prescriptions.map((ev) => {
            const keys = ['dosage', 'actualDosageUnitId', 'freqId', 'usageId', 'days', 'tqty', 'actualChargeUnitId']
            if (keys.some(key => !ev[key])) {
                flag = false;
                jumpKey.push(showIndex.westernMedicine);
                tips.push(t('prescriptionTable.westernMedicine.requiredMsg'));
            }
        });
    });
    // 中药: 总量，代加工
    pageGroupHerbalMedicine?.map((item) => {
        item.prescriptions.map((ev) => {
            if (!ev.tqty) {
                flag = false;
                jumpKey.push(showIndex.chineseHerbal);
                tips.push(t('prescriptionTable.chineseHerbal.requiredMsg'));
            }
        });
        if (item.isFeesProcessed) {
            if (!item.hospitalOrderProcess.hospitalOrderId) {
                flag = false;
                jumpKey.push(showIndex.chineseHerbal);
                tips.push(t('prescriptionTable.chineseHerbal.requiredMsg_2'));
            }
            if (!item.hospitalOrderProcess.processPrice && item.hospitalOrderProcess.processPrice !== 0) {
                flag = false;
                jumpKey.push(showIndex.chineseHerbal);
                tips.push(t('prescriptionTable.chineseHerbal.requiredMsg_3'));
            }
        }
    });
    // 输注: 
    // 附加项目: 总量
    // 群组: 剂量，剂量单位，频率，途径，天数，总量，总量单位
    pageGroupInjectionMedicine?.map((item) => {
        item.prescriptions.map((ev) => {
            if (item.groupNo === "0") {
                if (!ev.tqty) {
                    flag = false;
                    jumpKey.push(showIndex.injectionView);
                }
            } else {
                const keys = ['dosage', 'actualDosageUnitId', 'freqId', 'usageId', 'days', 'tqty', 'actualChargeUnitId']
                if (keys.some(key => !ev[key])) {
                    flag = false;
                    jumpKey.push(showIndex.injectionView);
                    tips.push(t('prescriptionTable.injectionView.requiredMsg'));
                }
            }
        });
    });
    // 检验检查: 总量，总量单位
    pageGroupInspection?.map((item) => {
        item.prescriptions.map((ev) => {
            if (!ev.tqty || !ev.actualChargeUnitId) {
                flag = false;
                jumpKey.push(showIndex.inspectionView);
                tips.push(t('prescriptionTable.inspectionView.requiredMsg'));
            }
        });
    });
    // 治疗: 总量，总量单位
    pageGroupTreatment?.map((item) => {
        item.prescriptions.map((ev) => {
            if (!ev.tqty || !ev.actualChargeUnitId) {
                flag = false;
                jumpKey.push(showIndex.disposalView);
                tips.push(t('prescriptionTable.disposalView.requiredMsg'));
            }
        });
    });
    // 手术: 总量，总量单位
    pageGroupSurgery?.map((item) => {
        item.prescriptions.map((ev) => {
            if (!ev.tqty || !ev.actualChargeUnitId) {
                flag = false;
                jumpKey.push(showIndex.operationView);
                tips.push(t('prescriptionTable.operationView.requiredMsg'));
            }
        });
    });
    jumpKey = _.uniq(jumpKey);
    tips = _.uniq(tips);
    if (tips.length > 0) {
        Message.warning(tips[0]);
    }
    if (jumpKey.length > 0) {
        jumpActiveIndex.value = jumpKey[0];
    }
    return flag;
}

/**
 * 下载文件 如果是本地，文件无法下载，均放置在打包后环境的nginx同级目录下
 * @param {String} url 下载地址
 */
export function download(url) {
    const link = document.createElement("a");
    link.setAttribute("target", "_blank");
    link.setAttribute("href", url);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// 获取单位下拉框
export function getUnitOptions(row, item) {
    let arr = [];
    if (item.typeInfo.type == 'zl') {
        arr = [
            {
                id: row.chargeUnitId,
                name: row.chargeUnitName,
                price: row.price,
                crkUnitType: 'max', // 库存用
                openType: 1, // 作业用
            },
            {
                id: row.minUnitId,
                name: row.minUnitName,
                price: row.dismantlePrice,
                crkUnitType: 'min',
                openType: 2,
            },
        ];
    } else if (item.typeInfo.type == 'jl') {
        arr = [
            {
                id: row.dosageUnitId,
                name: row.dosageUnitName,
            },
            { id: row.minUnitId, name: row.minUnitName },
        ];
    }
    let options = [];
    arr.forEach((it) => {
        if (it.id && !options.find((its) => its.id == it.id)) {
            options.push(it);
        }
    });
    return options;
};